// The Climate Resilience page: what has happened to the region since the film was
// made, and who is trying to do something about it.
//
// Every card links out. The images were supplied by Elaine from the articles
// themselves; if any rights holder objects, remove the image and the card
// still works, since the layout does not depend on one being present.

export const climateHeader = {
  image: '/assets/gap/laya-bhutan-boy-riding-yak.jpg',
  focus: '50% 55%',
  focusMobile: '32% 55%',
  alt: 'A boy riding a yak on the slopes above Laya, Bhutan, raising a hand towards the mountains',
}

export const climateGroups = [
  {
    title: 'What is happening',
    items: [
      {
        title: 'What is a glacial collapse, and how common is it?',
        source: 'Al Jazeera',
        date: 'August 2026',
        text: 'A section of glacier broke away above the Lhende river in Tibet. The ice and rock dammed the river, and when the blockage gave way the surge tore into Nepal\u2019s Rasuwa district. The Trishuli rose nine metres in thirty minutes.',
        image: '/assets/climate/nepal-rasuwa-glacial-flood-2026.jpg',
        alt: 'Buildings buried in mud after the glacial flood in Rasuwa, Nepal, August 2026',
        url: 'https://www.aljazeera.com/features/2026/8/27/nepal-tibet-floods-what-is-a-glacial-collapse-how-common-is-it',
      },
      {
        title: 'Himalayan glaciers are losing ice at double the rate since 2000',
        source: 'ICIMOD',
        date: 'March 2026',
        text: 'Fifty years of monitoring across the Hindu Kush Himalaya. Up to 27 metres of ice thickness gone since 1975, and nearly two billion people downstream. Bhutan is named as one of the region\u2019s blind spots, largely unmonitored.',
        image: '/assets/climate/hindu-kush-himalaya-glacier-retreat.jpg',
        alt: 'A glacier valley in the Hindu Kush Himalaya, with figures crossing the ice',
        url: 'https://www.icimod.org/press-releases/hindu-kush-himalaya-glaciers-losing-ice-at-double-the-rate-since-2000-new-icimod-report-confirm/',
      },
      {
        title: 'What is the Third Pole, and why does it matter?',
        source: 'World Economic Forum',
        date: 'December 2024',
        text: 'The Hindu Kush Himalaya holds the largest body of fresh water outside the poles, and the rivers that begin there feed close to two billion people. Its snow is also in the wheat, maize, rice and sugar most of the world eats, whether or not they have heard of it.',
        image: '/assets/climate/hindu-kush-himalaya-the-third-pole.jpg',
        alt: 'The peaks of the Hindu Kush Himalaya above cloud, the region known as the Third Pole',
        url: 'https://www.weforum.org/stories/nature-and-biodiversity/care-the-third-pole-crucial-role-global-water-resource/',
      },
    ],
  },
  {
    title: 'What you can do',
    items: [
      {
        title: 'Nineteen ways to help the climate, ranked',
        source: 'World Resources Institute',
        date: 'October 2025',
        text: 'Ranked by evidence rather than by what feels virtuous. Living car-free, flying less and switching to renewable energy at home come out top; going car-free is 78 times more effective than composting. The world\u2019s largest climate survey also found that 80% of people want their countries to do more.',
        image: '/assets/climate/individual-climate-actions-ranked.jpg',
        alt: 'An illustration of the everyday choices that carry the most weight: flying, food, heating and light',
        url: 'https://www.wri.org/insights/climate-friendly-choices-ranked',
      },
      {
        title: 'Beyond voting: how to take citizen action for the climate',
        source: 'Project Drawdown',
        date: 'November 2024',
        text: 'Acting as a citizen rather than as a consumer. How to work out where your concern fits alongside everything else happening around you, who your unlikely allies are, and how to make the case without being tone-deaf to the rest of it.',
        image: '/assets/climate/citizen-action-for-the-climate.jpg',
        alt: 'Two people shaking hands, a figure for collective action on the climate',
        url: 'https://drawdown.org/insights/beyond-voting-heres-how-to-take-citizen-action-for-the-climate',
      },
      {
        title: '2026 is Singapore\u2019s Year of Climate Adaptation',
        source: 'Ministry of Sustainability and the Environment',
        date: '2026',
        text: 'Closer to home. The SG Eco Fund has five million dollars for community-led projects on heat resilience, flood protection, water conservation and local produce, open year-round, and the National Adaptation Plan is open to public comment.',
        image: '/assets/climate/singapore-year-of-climate-adaptation-2026.jpg',
        alt: 'The Year of Climate Adaptation emblem, Singapore 2026',
        url: 'https://www.mse.gov.sg/yoca/',
      },
    ],
  },
  {
    title: 'The wider problem',
    items: [
      {
        title: 'Nepal\u2019s glaciers will flood again. Can scientists predict the next one?',
        source: 'National Geographic',
        date: 'September 2026',
        text: 'As the Himalaya thaws, ground that held for centuries is coming loose. The same happened above the Swiss village of Blatten in 2025, buried under twenty million tons of rock and ice. Nobody there died: the glacier had been watched since the 1990s, and the village was evacuated nine days before it fell.',
        image: '/assets/climate/himalaya-aerial-glacier-range.jpg',
        alt: 'Aerial view across the Himalaya, a glacier running between snow peaks',
        url: 'https://www.nationalgeographic.com/environment/article/nepal-glacier-floods-warming-climate-disaster',
      },
    ],
  },
  {
    title: 'Bhutan and its glaciers',
    items: [
      {
        title: 'Thorthormi Lake and the growing risk of glacial floods',
        source: 'The Bhutanese',
        date: 'September 2025',
        text: 'The moraine wall holding Thorthormi apart from Raphstreng has thinned from 45 metres to 33. A siphon is being trialled that needs no fuel and can work through winter, because the lake no longer freezes solid.',
        image: '/assets/climate/thorthormi-raphstreng-luggye-glacial-lakes-bhutan.jpg',
        alt: 'Satellite view of Thorthormi, Raphstreng and Luggye glacial lakes above the Pho Chu river, Bhutan',
        url: 'https://thebhutanese.bt/himalayan-warnings-thorthormi-lake-and-the-growing-risk-of-glacial-floods/',
      },
      {
        title: 'Living in constant fear of a glacial lake outburst flood',
        source: 'UNDP Bhutan',
        date: '',
        text: 'Lunana, a remote highland community nestled among glaciers, where climate change is an everyday reality.',
        image: '/assets/climate/lunana-bhutan-highland-women-walking.jpg',
        alt: 'Two women walking the highlands of Lunana, Bhutan, below the mountains',
        url: 'https://undp-bhutan.exposure.co/living-in-constant-fear-of-glacial-lake-outburst-flood',
      },
      {
        title: 'Glacial lake floods prevented in Bhutan',
        source: 'UNDP',
        date: '',
        text: 'Every year from June to September, between two and three hundred people trek to Thorthormi glacial lake to drain water from it by hand, with pickaxes and shovels.',
        image: '/assets/climate/prayer-flags-above-the-himalaya-bhutan.jpg',
        alt: 'Prayer flags strung against the peaks of the Himalaya',
        url: 'https://www.youtube.com/watch?v=lbgv4ZMF5Zk',
      },
      {
        title: 'Dwindling cordyceps and a bleak future for Lunana\u2019s women',
        source: 'UNDP Bhutan',
        date: '',
        text: 'Lunana has warmed a great deal in the last decade, and the cordyceps harvest that sustains its households is disappearing with the cold.',
        image: '/assets/climate/lunana-bhutan-woman-carrying-firewood.jpg',
        alt: 'A woman of Lunana carrying firewood, with the glacier behind her',
        url: 'https://undp-bhutan.exposure.co/climate-change-exacerbates-lunana-womens-insecurity',
      },
      {
        title: 'Training Bhutanese experts to analyse climate risk',
        source: 'Eurac Research',
        date: '',
        text: 'A research team travels to Bhutan to train local experts, and returns with the view that the Himalayan state has a great deal to teach.',
        image: '/assets/climate/prayer-flags-and-snow-peaks-bhutan.jpg',
        alt: 'Prayer flags in the wind above a view of the Bhutanese Himalaya',
        url: 'https://www.eurac.edu/en/magazine/climate-risks-the-case-of-bhutan',
      },
      {
        title: 'Melting glaciers in Bhutan',
        source: 'BBC',
        date: '',
        text: 'Ade Adepitan visits Bhutan to see the effect of rising temperatures on its glaciers, and the danger that poses downstream.',
        image: '/assets/climate/bhutan-glacial-lake-below-the-snowline.jpg',
        alt: 'A turquoise glacial lake below the snowline in Bhutan',
        url: 'https://www.bbc.co.uk/teach/class-clips-video/articles/zg4ntrd',
      },
    ],
  },
  {
    title: 'Worth your time',
    items: [
      {
        title: 'Bhutan Royal Highland Festival',
        source: 'Laya, Gasa',
        date: 'Every October',
        text: 'Where the film was made. Founded in 2016 under the Royal Vision, it gathers highland communities from eleven gewogs across ten dzongkhags, whose way of life depends on the ice above them.',
        image: '/assets/climate/royal-highland-festival-laya-bhutan-dance.jpg',
        alt: 'Dancers in traditional dress at the Royal Highland Festival in Laya, Bhutan',
        url: 'https://laya.bt/',
      },
      {
        title: 'The Last Glaciers film',
        source: 'Craig Leeson and Malcolm Wood',
        date: '2022',
        text: 'Filmed over four years in twelve countries, on the relationship between climate change, mountain environments and glaciers. From the director of A Plastic Ocean.',
        image: '/assets/climate/the-last-glaciers-documentary.jpg',
        alt: 'The Last Glaciers, a documentary on climate change and mountain glaciers',
        url: 'https://thelastglaciers.com/',
      },
    ],
  }
]
